import React, { useState } from 'react';
import { 
  PlusIcon, 
  EyeIcon, 
  EyeSlashIcon,
  TrashIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline';
import { mockApiKeyUsage } from '../../services/mockData';
import { ApiKeyUsage } from '../../types';

export const ApiKeys: React.FC = () => {
  const [showKeys, setShowKeys] = useState<{ [key: string]: boolean }>({});
  const [keys] = useState<ApiKeyUsage[]>(mockApiKeyUsage);

  const toggleKeyVisibility = (keyId: string) => {
    setShowKeys(prev => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  const formatCurrency = (value: number) => `$${value.toFixed(2)}`;
  const formatDate = (dateString: string) => 
    new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });

  const maskApiKey = (key: string) => {
    if (showKeys[key]) return key;
    return key.substring(0, 8) + '•'.repeat(20);
  };

  const getStatusBadge = (status: ApiKeyUsage['status']) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-yellow-100 text-yellow-800',
      expired: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">API Keys</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Manage your Ask Engine API keys and monitor their usage</p>
        </div>
        <button className="btn-primary flex items-center">
          <PlusIcon className="h-4 w-4 mr-2" />
          Create New Key
        </button>
      </div>

      {/* API Keys List */}
      <div className="chart-container">
        <div className="space-y-4">
          {keys.map((key) => (
            <div key={key.apiKey} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{key.keyName}</h3>
                    {getStatusBadge(key.status)}
                  </div>
                  
                  <div className="flex items-center space-x-2 mb-4">
                    <code className="bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded text-sm font-mono text-gray-900 dark:text-gray-100">
                      {maskApiKey(key.apiKey)}
                    </code>
                    <button
                      onClick={() => toggleKeyVisibility(key.apiKey)}
                      className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
                      title={showKeys[key.apiKey] ? 'Hide key' : 'Show key'}
                    >
                      {showKeys[key.apiKey] ? (
                        <EyeSlashIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => navigator.clipboard.writeText(key.apiKey)}
                      className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
                      title="Copy to clipboard"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Requests</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{key.requests.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Tokens</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{(key.tokens / 1000).toFixed(0)}k</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Cost</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(key.cost)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Last Used</p>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{formatDate(key.lastUsed)}</p>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-2 ml-4">
                  <button className="btn-secondary text-sm">
                    Edit
                  </button>
                  <button className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Usage Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="metric-card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Total Usage</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Requests</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {keys.reduce((sum, key) => sum + key.requests, 0).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Tokens</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {(keys.reduce((sum, key) => sum + key.tokens, 0) / 1000).toFixed(0)}k
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Cost</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {formatCurrency(keys.reduce((sum, key) => sum + key.cost, 0))}
              </span>
            </div>
          </div>
        </div>

        <div className="metric-card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Key Status</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Active Keys</span>
              <span className="font-semibold text-green-600 dark:text-green-400">
                {keys.filter(key => key.status === 'active').length}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Suspended Keys</span>
              <span className="font-semibold text-yellow-600 dark:text-yellow-400">
                {keys.filter(key => key.status === 'suspended').length}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Expired Keys</span>
              <span className="font-semibold text-red-600 dark:text-red-400">
                {keys.filter(key => key.status === 'expired').length}
              </span>
            </div>
          </div>
        </div>

        <div className="metric-card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Security</h3>
          <div className="space-y-3">
            <button className="w-full btn-secondary text-left text-sm">
              Rotate All Keys
            </button>
            <button className="w-full btn-secondary text-left text-sm">
              Set Rate Limits
            </button>
            <button className="w-full btn-secondary text-left text-sm">
              View Access Logs
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};